import { atom } from 'jotai';
import { atomFamily, atomWithStorage } from 'jotai/utils';

import CurrentFacilityAtoms from '@/state/current-facility.state';

import {
  LEGACY_VERSION_FIELDS,
  UXDS_ROLLBACK_NAMESPACE_SET,
  type UxdsRollbackNamespace,
} from './uxds-version-rollback-namespaces';

/**
 * Persisted UXDS version rollback selections for dev preview.
 * Scoped per facility; cleared automatically when the facility changes.
 */
export type UxdsVersionRollbackStorage = {
  facilityShortname: string;
  versions: Partial<Record<UxdsRollbackNamespace, number>>;
} | null;

type LegacyUxdsVersionRollbackStorage = {
  facilityShortname: string;
  processViewVersion?: number;
  facilityMapVersion?: number;
  versions?: Partial<Record<string, number>>;
};

export function normalizeUxdsVersionRollbackStorage(
  raw: LegacyUxdsVersionRollbackStorage | UxdsVersionRollbackStorage | null
): UxdsVersionRollbackStorage {
  if (!raw) {
    return null;
  }

  if (raw.versions && !('processViewVersion' in raw) && !('facilityMapVersion' in raw)) {
    const versions: Partial<Record<UxdsRollbackNamespace, number>> = {};
    for (const [key, value] of Object.entries(raw.versions)) {
      if (value != null && UXDS_ROLLBACK_NAMESPACE_SET.has(key)) {
        versions[key as UxdsRollbackNamespace] = value;
      }
    }
    return { facilityShortname: raw.facilityShortname, versions };
  }

  const legacy = raw as LegacyUxdsVersionRollbackStorage;
  const versions: Partial<Record<UxdsRollbackNamespace, number>> = {};

  for (const [legacyField, rollbackKey] of Object.entries(LEGACY_VERSION_FIELDS)) {
    const value = legacy[legacyField as keyof typeof LEGACY_VERSION_FIELDS];
    if (value != null) {
      versions[rollbackKey] = value;
    }
  }

  if (legacy.versions) {
    for (const [key, value] of Object.entries(legacy.versions)) {
      if (value != null && UXDS_ROLLBACK_NAMESPACE_SET.has(key)) {
        versions[key as UxdsRollbackNamespace] = value;
      }
    }
  }

  return { facilityShortname: legacy.facilityShortname, versions };
}

const uxdsVersionRollbackStorageBaseAtom = atomWithStorage<UxdsVersionRollbackStorage>(
  'uxds-version-rollback',
  null
);

export const uxdsVersionRollbackStorageAtom = atom(
  (get) => {
    const raw = get(uxdsVersionRollbackStorageBaseAtom);
    const storage = normalizeUxdsVersionRollbackStorage(raw);
    const currentFacility = get(CurrentFacilityAtoms.facility);

    if (
      storage &&
      currentFacility?.shortName &&
      storage.facilityShortname !== currentFacility.shortName
    ) {
      return null;
    }

    return storage;
  },
  (_get, set, newValue: UxdsVersionRollbackStorage) => {
    set(uxdsVersionRollbackStorageBaseAtom, newValue);
  }
);
uxdsVersionRollbackStorageAtom.debugLabel = 'uxdsVersionRollbackStorageAtom';

export const rollbackVersionAtomFamily = atomFamily((namespace: UxdsRollbackNamespace) => {
  const versionAtom = atom((get) => get(uxdsVersionRollbackStorageAtom)?.versions?.[namespace]);
  versionAtom.debugLabel = `rollbackVersionAtom(${namespace})`;
  return versionAtom;
});

/**
 * True when a rollback version is active for any registered namespace.
 */
export const hasUxdsVersionRollbackAtom = atom((get) => {
  const storage = get(uxdsVersionRollbackStorageAtom);
  return Object.entries(storage?.versions ?? {}).some(
    ([ns, v]) => v != null && UXDS_ROLLBACK_NAMESPACE_SET.has(ns)
  );
});
hasUxdsVersionRollbackAtom.debugLabel = 'hasUxdsVersionRollbackAtom';

export const UXDS_ROLLBACK_EDIT_DISABLED_TOOLTIP =
  'Clear version rollback in Developer Tools to edit';
